<template>
  <div
    v-click-outside="vsoConfig"
    class="f-dropdown-overlay"
    :class="{reverse, variable}"
    @mousedown="noPropagation ? closeDropdown($event) : hideDropdown($event)"
  >
    <div
      class="wrapper"
      :style="dropdownDisplay"
      :class="{reverse}"
    >
      <FInpput
        v-if="!noSearch"
        ref="search"
        v-debounce:300="
          (val) => {
            slugList = [];
            search = val;
            $emit('filter', val);
          }
        "
        class="clickable f-head"
        icon="search"
        :value-in="search"
        :class="{
          small,
          bordered: minSearchLength && minSearchLength > search.length,
          reverse,
        }"
        :max-height="styleSet ? styleSet.hh : dropdownStyle.hh"
        :placeholder="placeholder || 'Rechercher ici ...'"
        clear
        no-prompt
        @clear="search = ''"
      />
      <div
        v-if="!minSearchLength || minSearchLength <= search.length"
        :style="
          noSearch
            ? 'height: 100%'
            : `height: calc(100%  - ${headerMargin + 2}px)`
        "
        :class="[
          'f-dropdown',
          (dropdownStyle && dropdownStyle.left) || (styleSet && styleSet.left)
            ? 'left-border-radius'
            : '',
          reverse ? 'reverse' : '',
        ]"
      >
        <slot
          v-if="emptyPlaceholder && !list.length"
          name="empty"
        />
        <slot v-if="!showSelected" />
        <div
          v-if="multiselect && showSelected"
          class="text-aubergine f-dropdown-adder"
        >
          <div class="text-gray translate">Eléments sélectionnés</div>
          <a
            class="clickable text-aubergine-slim translate"
            @click.stop="
              showSelected = false;
              staySelected = selected;
            "
          >
            Retour
          </a>
        </div>
        <div
          v-if="!noStickyHead"
          class="f-slug"
          :class="
            elementToDisplay && elementToDisplay.length > 0 ? 'slug-shown' : ''
          "
        >
          <transition-group name="list">
            <div
              v-for="(el, i) in elementToDisplay"
              :key="`fsn-${i}`"
              class="f-slug-name"
              :class="{translate: translate}"
            >
              {{ el }}
            </div>
          </transition-group>
        </div>
        <div
          v-else-if="list && list.list && (!maxLevel || maxLevel > 1)"
          class="f-slug"
        />
        <div
          ref="holder"
          class="f-elements-holder"
          :class="{
            'shallow-top':
              !noStickyHead ||
              (list && list.list && (!maxLevel || maxLevel > 1)),
          }"
        >
          <RecursiveList
            :deepest="deepest"
            :disable-adding="disableAdding"
            :filter="!ignoreFilter ? search : ''"
            :filter-selected="showSelected"
            :forecast-enabled-only="forecastEnabledOnly"
            :list="list"
            :max-level="maxLevel"
            :max-select-level="maxSelectLevel"
            :multiselect="multiselect"
            :only-alterable="onlyAlterable"
            :selected="selected"
            :stay-selected="staySelected"
            :select-deepest="selectDeepest"
            :slug-list="slugList"
            :variable="variable"
            :translate="translate"
            @add-element="$emit('addElement', $event)"
            @hidden="elementToDisplay = $event"
            @icon="$emit('icon', $event)"
            @select="
              multiselect ? setSelected($event) : $emit('select', $event)
            "
            @custom-select="
              multiselect ? setSelected($event) : $emit('customSelect', $event)
            "
            @slug="slugList.push($event)"
          />
        </div>
        <div
          v-if="!multiselect && !disableAdding"
          class="text-aubergine f-dropdown-adder"
        >
          <a
            class="clickable"
            @click.stop="$emit('addElement', [])"
          >
            <FontAwesomeIcon icon="plus" />
            <span
              v-if="adder"
              class="translate"
            >
              {{ adder }}
            </span>
            <span
              v-else
              class="translate"
              >Ajouter une catégorie</span
            >
          </a>
        </div>
        <slot
          v-if="!showSelected"
          name="bottom"
        />
        <div
          v-if="
            multiselect &&
            !showSelected &&
            ((emptyPlaceholder && list.length) || !emptyPlaceholder) &&
            !noButtons
          "
          class="text-aubergine f-dropdown-adder vertical"
          :class="{compact: compactButton}"
        >
          <a
            v-if="selected.length && !noSelectedList"
            class="clickable selected text-aubergine-slim translate"
            @click.stop="showSelected = true"
          >
            Eléments sélectionnés
          </a>
          <span
            v-else-if="!noSelectedList"
            style="height: 19px; width: 10px"
          />
          <a
            v-if="compactButton"
            class="clickable text-aubergine small-valid translate"
            @click="
              $emit('selected', selected);
              sthChanged = false;
            "
          >
            Valider
          </a>
          <FygrButton
            v-else
            class="f-dd-confirm translate"
            color="orange"
            :disabled="(!selected.length && !sthChanged) || !sthChanged"
            @click="
              $emit('selected', selected);
              sthChanged = false;
            "
          >
            Valider
          </FygrButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapState} from 'vuex';
import vClickOutside from 'click-outside-vue3';
import {nextTick} from 'vue';
import vueDebounce from 'vue-debounce';
import RecursiveList from '@/components/elements/RecursiveList.vue';
import FInpput from '@/components/elements/FygrInput.vue'; // force import to use focus on refs

/**
 * CategoriesDropdown properties.
 * @prop {String} Adder - Text for adder line.
 * @prop {Boolean} disableAdding - Disable possibility to add new sub-entry.
 * @prop {Object} dropdownStyle - Dropdown position (required, check InvoiceWrapper to find
      how it looks).
 * @prop {Array} list - List of entries (required).
 * @prop {Number} maxLevel - Max level of depth in list.
 * @prop {Number} maxSelectLevel - Max level that possible to select.
 * @prop {Boolean} multiselect - Enable selecting multiple entries.
 * @prop {Boolean} noPropagation - Stop 'close' event propagation.
 * @prop {String} placeholder - Custom placeholder for input.
 * @prop {Array} preselected - List of preselected entries (temporary not supported).
 * @prop {Boolean} selectDeepest - Enables selecting only deepest entries level (maxLevel required).
 * @prop {Boolean} small - Change size of header.
 */
export default {
  name: 'CategoriesDropdownRectangle',
  components: {
    RecursiveList,
    FInpput,
  },
  directives: {
    clickOutside: vClickOutside.directive,
    debounce: vueDebounce({lock: true}),
  },
  props: {
    adder: {
      type: String,
      default: '',
    },
    ignoreFilter: Boolean,
    compactButton: Boolean,
    deepest: Boolean,
    disableAdding: Boolean,
    dropdownStyle: {
      type: Object,
      default: () => ({
        'max-height': 440,
        height: 400,
        hh: 40,
      }),
    },
    emptyPlaceholder: Boolean,
    forecastEnabledOnly: Boolean,
    list: {
      type: Array,
      default: () => [],
    },
    maxLevel: {
      type: Number,
      default: null,
    },
    maxSelectLevel: {
      type: Number,
      default: null,
    },
    minSearchLength: {
      type: Number,
      default: null,
    },
    multiselect: Boolean,
    noButtons: Boolean,
    noSelectedList: Boolean,
    noStickyHead: Boolean,
    noPropagation: Boolean,
    onlyAlterable: Boolean,
    placeholder: {
      type: String,
      default: '',
    },
    presearch: {
      type: String,
      default: '',
    },
    preselected: {
      type: Array,
      default: () => [],
    },
    eventContainer: {
      type: Object,
      default: () => {},
    },
    selectDeepest: Boolean,
    emitChanges: Boolean,
    small: Boolean,
    variable: Boolean,
    translate: {
      type: Boolean,
      default: false,
    },
    noSearch: {
      type: Boolean,
      default: false,
    },
    align: {
      type: String,
      default: 'right',
    },
  },
  emits: [
    'filter',
    'addElement',
    'icon',
    'select',
    'customSelect',
    'addElement',
    'close',
    'selected',
    'updateSelected',
  ],
  data() {
    return {
      styleSet: {},
      blockAssign: false,
      elementToDisplay: [],
      search: '',
      staySelected: [],
      selected: [],
      slugList: [],
      showSelected: false,
      sthChanged: false,
      vsoConfig: {
        handler: () => this.$emit('close'),
        events: ['mousedown'],
      },
    };
  },
  computed: {
    ...mapState('app', ['categories', 'scaleRatio']),
    headerMargin() {
      return this.styleSet && Object.keys(this.styleSet).length
        ? this.styleSet?.hh + (this.compactButton ? 20 : 6)
        : this.dropdownStyle?.hh + (this.compactButton ? 20 : 6);
    },
    reverse() {
      const source = Object.keys(this.styleSet).length
        ? this.styleSet
        : this.dropdownStyle;
      const height = source['max-height'] || source.height;
      return (
        source.bottom && !(source.bottom - height - 12 / this.scaleRatio > 0)
      );
    },
    //
    // IMPORTANT! to achieve correct positioning of dropdown (especially when we use it like wrapper) we need to adjust 'max-height' property in
    // component that uses this dropdown, otherwise calculation will be provided based on default 260px height. Example - if DD is used
    // as wrapper with <slot> and you want to have 300px of its height you need use  style: {...  'max-height': 300 ...}
    //
    dropdownDisplay() {
      const source = Object.keys(this.styleSet).length
        ? this.styleSet
        : this.dropdownStyle;
      const dict = {
        top: `${source.top + 5}px`,
        width: `${source.width}px`,
        'min-width': `${source['min-dd-width'] || source['min-width']}px`,
        'max-width': `${source['max-width']}px`,
        'max-height': `${source['max-height']}px`,
        height: `${source.height}px`,
        'margin-top': `${source['margin-top']}px`,
      };
      const minWidth =
        parseFloat(source['min-dd-width'] || source['min-width']) || 0;
      const maxWidth = parseFloat(source['max-width']) || Infinity;
      const effectiveWidth = Math.min(
        Math.max(minWidth, parseFloat(source.width) || 0),
        maxWidth,
      );
      const triggerRight = window.innerWidth - (parseFloat(source.right) || 0);
      if (this.align === 'left' || triggerRight < effectiveWidth) {
        dict.left = `${Math.max(0, source.left || 0)}px`;
      } else {
        dict.right = `${source.right}px`;
      }
      if (this.reverse) {
        dict.top = 'unset';
        const height = source['max-height'] || source.height;
        const shift =
          source.innerHeight -
          75 / this.scaleRatio -
          source.bottom -
          height -
          12 / this.scaleRatio -
          source.targetHeight -
          5;
        if (shift < 0) {
          dict.bottom = `${source.bottom + source.targetHeight + shift}px`;
        } else {
          dict.bottom = `${source.bottom + 5 + source.targetHeight}px`;
        }
      }
      return dict;
    },
  },
  watch: {
    list: {
      handler() {
        this.slugList = [];
      },
      deep: true,
    },
    preselected: {
      handler() {
        this.sthChanged = true;
        this.selected = [...this.preselected];
        this.staySelected = [...this.preselected];
      },
      deep: true,
    },
    search() {
      this.blockAssign = true;
      setTimeout(() => {
        this.slugList = [];
        this.elementToDisplay = [];
        this.blockAssign = false;
      }, 100);
    },
    selected: {
      handler() {
        if (
          JSON.stringify(this.selected) !== JSON.stringify(this.preselected)
        ) {
          this.sthChanged = true;
        }
      },
      deep: true,
    },
    elementToDisplay() {
      if (
        this.blockAssign &&
        this.elementToDisplay &&
        this.elementToDisplay.length
      ) {
        this.elementToDisplay = [];
      }
    },
    eventContainer: {
      deep: true,
      handler(newValue) {
        if (newValue && Object.keys(newValue).length) {
          this.setStyle(newValue);
        }
      },
    },
  },

  created() {
    if (this.eventContainer && Object.keys(this.eventContainer).length) {
      this.setStyle(this.eventContainer);
    }
  },
  mounted() {
    nextTick(() => {
      this.$refs?.search?.focus();
      if (this.eventContainer && Object.keys(this.eventContainer).length) {
        requestAnimationFrame(() => this.setStyle(this.eventContainer));
      }
    });
    this.search = this.presearch ? this.presearch : '';
    if (this.preselected) {
      this.selected = [...this.preselected];
      this.staySelected = [...this.preselected];
    }
  },
  methods: {
    setStyle({e, container, style}) {
      const trigger =
        (e?.target?.closest && e.target.closest(container)) ||
        e?.currentTarget ||
        e?.target ||
        null;
      const rect = trigger?.getBoundingClientRect?.() ?? {
        top: e?.clientY ?? 0,
        bottom: e?.clientY ?? 0,
        left: e?.clientX ?? 0,
        right: e?.clientX ?? 0,
        width: 0,
        height: 0,
      };
      const viewWidth = e?.view?.innerWidth ?? window.innerWidth;
      const viewHeight = e?.view?.innerHeight ?? window.innerHeight;
      const ratio = this.scaleRatio || 1;
      this.styleSet = {
        top: rect.bottom,
        right: viewWidth - rect.right,
        left: rect.left,
        width: rect.width - 10,
        bottom: viewHeight - rect.bottom,
        height: 400 / ratio,
        hh: 35 / ratio,
        targetHeight: rect.height,
        innerHeight: viewHeight,
        ...style,
      };
    },
    hideDropdown(e) {
      if (e.target.closest('.f-head') || e.target.closest('.f-dropdown')) {
        return;
      }
      this.$emit('close');
    },
    closeDropdown(e) {
      e.stopPropagation();
      this.$emit('close');
    },
    setValue(val) {
      this.$emit('selected', val);
    },
    setSelected(id) {
      if (this.selected.includes(id)) {
        const idx = this.selected.findIndex((i) => i === id);
        this.selected.splice(idx, 1);
        this.staySelected.splice(idx, 1);
      } else {
        this.selected.push(id);
        this.staySelected.push(id);
      }
      this.$emit('updateSelected', this.selected, id);
      if (this.emitChanges) {
        this.$emit('select', id);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@use 'sass:color';
.f-dropdown-overlay.variable {
  position: fixed;
  z-index: 999;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  bottom: 0;
  background-color: rgba(rgba(0, 0, 0, 0), $alpha: 0);
  .wrapper {
    transform-origin: top;
    position: absolute;
    background: var(--fygr-surface-raised);
    padding: var(--px5);
    border-radius: var(--px4);
    display: flex;
    flex-direction: column;
    border: 1px solid var(--fygr-line);
    box-shadow: 0 1px 8px rgba(46, 7, 41, 0.1);
    &.reverse {
      flex-direction: column-reverse;
    }
    .f-head {
      background: var(--fygr-surface-raised);
      border: 1px solid var(--fygr-line);
      border-radius: var(--px4);
      z-index: 100;

      box-sizing: border-box;
      &.reverse {
        padding-top: var(--px1);
      }
      &.bordered {
        border-radius: var(--px4);
      }
      &.small {
        :deep(.prefixed-input) {
          height: var(--px30);
        }

        width: var(--px60);
        margin-top: var(--px11);
        border-radius: var(--px4) var(--px4) 0 0;
      }
      :deep(.prefixed-input) {
        height: var(--px34);
        max-width: calc(100% - var(--px2));
        width: 100%;
        svg {
          font-size: var(--px14);
          top: 50%;
          transform: translateY(-50%);
          margin-top: 0;
          &:first-child {
            left: var(--px15);
          }
          &:last-child {
            right: var(--px15);
          }
        }
        input {
          margin-left: calc(var(--px1) * -1);
          // override the inline min/max-height (= maxHeight prop, 35px) so the
          // input fits inside the 32px f-head box and stops covering its bottom border
          max-height: var(--px30) !important;
          min-height: var(--px30) !important;
          font-size: var(--px14);
          padding: 0 var(--px45);
          border: none;
          background: transparent;
        }
      }
      svg {
        margin-left: auto;
        color: var(--fygr-text-fade);
      }
      :deep(.prefixed-input) {
        z-index: 101;
      }
    }
    .f-dropdown {
      margin-top: var(--px5);
      margin-bottom: 0;
      z-index: 100;
      display: flex;
      flex-direction: column;
      top: var(--px211);
      right: var(--px38);
      background-color: var(--fygr-surface-raised);
      border-radius: var(--px4) 0 var(--px4) var(--px4);
      &.reverse {
        border-radius: var(--px4) var(--px4) 0 var(--px4);
        margin-top: 0;
        margin-bottom: var(--px2);
      }
      &.left-border-radius {
        border-radius: 0 var(--px4) var(--px4) var(--px4);
      }
      .f-dropdown-adder {
        display: flex;
        height: var(--px50);
        padding: 0 var(--px20);
        align-items: center;
        justify-content: space-between;
        font-size: var(--px14);
        &.vertical {
          flex-direction: column;
          align-items: flex-start;
          margin-top: var(--px10);
          height: var(--px100);
          &.compact {
            height: var(--px38);
          }
          .f-dd-confirm {
            margin: var(--px20) auto;
            height: var(--px32);
            width: var(--px150);
          }
        }
        .small-valid {
          margin-left: auto;
          display: flex;
          font-weight: 600;
          flex: 0;
        }
        svg {
          margin-right: var(--px10);
        }
        .selected {
          font-weight: normal;
        }
      }
      .f-slug {
        display: flex;
        align-items: center;
        justify-content: space-between;
        height: auto;
        width: calc(100% - var(--px8));
        background-color: unset;
        gap: var(--px3);
        .f-slug-name {
          width: 100%;
          display: flex;
          word-wrap: break-word;
          overflow-wrap: break-word;
          font-size: var(--px12);
          color: var(--fygr-text-fade);

          &:nth-child(2) {
            justify-content: center;
          }
          &:last-child {
            justify-content: end;
            margin-right: var(--px5);
          }
          &:first-child {
            justify-content: start;
            margin-left: var(--px5);
          }
        }
      }
      .slug-shown {
        background-color: var(--fygr-surface-raised);
        padding-bottom: var(--px3);
        border-bottom: 1px solid var(--fygr-line);
      }
      .shallow-top {
        margin-top: 0;
      }
      .f-elements-holder {
        width: 100%;
        height: 100%;
        overflow: auto;
        &.small {
          max-height: var(--px210);
        }
        .f-element {
          height: var(--px32);
          display: flex;
          width: calc(100% - var(--px5));
          justify-content: center;
          align-items: flex-end;
          border-bottom: var(--px1) solid var(--fygr-line);
          padding: 0 var(--px5);
          flex-direction: column;
          &:hover {
            background-color: var(--fygr-selected-bg);
            color: var(--fygr-selected-ink);
          }
        }
      }
    }
  }
}
.f-dropdown-overlay {
  position: fixed;
  z-index: 99;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  bottom: 0;
  background-color: rgba(rgba(0, 0, 0, 0), $alpha: 0);
  .wrapper {
    position: absolute;
    background: var(--fygr-surface-raised);
    padding: var(--px5);
    border-radius: var(--px4);
    display: flex;
    flex-direction: column;
    border: 1px solid var(--fygr-line);
    .devider {
      width: 100%;
      background: var(--fygr-line);
      height: 1px;
      margin: var(--px8) 0;
    }
    &.reverse {
      flex-direction: column-reverse;
      &::after {
        top: 100%;
        transform: translateX(-50%);
      }
      &::before {
        top: 100%;
        transform: translateX(-50%);
      }
    }
    .f-head {
      background: var(--fygr-bg-soft);
      align-items: center;
      border-radius: var(--px4);
      z-index: 100;

      box-sizing: border-box;
      &.reverse {
        padding-top: var(--px1);
      }
      &.bordered {
        border-radius: var(--px4);
      }
      &.small {
        width: var(--px60);
        margin-top: var(--px11);
        border-radius: var(--px4) var(--px4) 0 0;
        :deep(.prefixed-input) {
          height: var(--px30);
        }
      }
      :deep(.prefixed-input) {
        max-width: calc(100% - var(--px2));
        height: var(--px32);
        width: 100%;
        svg {
          margin-top: calc(var(--px1) * -1);
        }
        input {
          max-height: var(--px32);
          min-height: var(--px32);
          border: none;
        }
      }
      svg {
        margin-left: auto;
        color: var(--fygr-text-fade);
      }
      :deep(.prefixed-input) {
        z-index: 101;
      }
    }
    .f-dropdown {
      margin-top: var(--px5);
      margin-bottom: 0;
      z-index: 100;
      display: flex;
      flex-direction: column;
      top: var(--px211);
      right: var(--px38);
      background-color: var(--fygr-surface-raised);
      border-radius: var(--px4) 0 var(--px4) var(--px4);

      &.reverse {
        border-radius: var(--px4) var(--px4) 0 var(--px4);
        margin-top: 0;
        margin-bottom: var(--px5);
      }
      &.left-border-radius {
        border-radius: 0 var(--px4) var(--px4) var(--px4);
      }
      .f-dropdown-adder {
        display: flex;
        height: var(--px50);
        padding: 0 var(--px20);
        align-items: center;
        justify-content: space-between;
        &.vertical {
          flex-direction: column;
          align-items: flex-start;
          margin-top: var(--px10);
          height: var(--px100);
          &.compact {
            height: var(--px38);
          }
          .f-dd-confirm {
            margin: var(--px20) auto;
            height: var(--px32);
            width: var(--px150);
          }
        }
        .small-valid {
          margin-left: auto;
          display: flex;
          font-weight: 600;
          flex: 0;
        }
        svg {
          margin-right: var(--px10);
        }
        .selected {
          font-weight: normal;
        }
      }
      .f-slug {
        display: flex;
        align-items: center;
        justify-content: space-between;
        top: var(--px10);
        height: var(--px40);
        width: calc(100% - var(--px10));
        background-color: var(--fygr-surface-raised);
        .f-slug-name {
          display: flex;
          word-wrap: break-word;
          overflow-wrap: break-word;
          font-size: var(--px12);
          color: var(--fygr-text-fade);
          &:first-child {
            margin-left: var(--px20);
          }
          &:last-child {
            margin-right: var(--px20);
          }
        }
      }
      .f-elements-holder {
        width: 100%;
        height: 100%;
        overflow: auto;
        &.small {
          max-height: var(--px210);
        }
        .f-element {
          height: var(--px32);
          display: flex;
          width: calc(100% - var(--px50));
          justify-content: center;
          align-items: flex-end;
          border-bottom: 1px solid var(--fygr-line);
          padding: 0 var(--px5);
          flex-direction: column;
          &:hover {
            background-color: var(--fygr-selected-bg);
            color: var(--fygr-selected-ink);
          }
        }
      }
    }
  }
}
.list-enter-active,
.list-leave-active {
  transition: all 0.2s ease;
}
.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateX(2px);
}
</style>
